import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import {
  email,
  form,
  FormField,
  maxLength,
  minLength,
  pattern,
  required,
} from '@angular/forms/signals';
import { RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AppSvgIconComponent,
  RequestState,
  SignalButton,
  SignalFormDirective,
  SignalTextField,
  State,
  validateSignalForm,
} from '@vallift/ngl';
import { environment } from '@environments/environment';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import { TokenStorageService } from '@core/services/token-storage.service';
import { navigateAfterLogin, navigateAfterUserAuth } from '@core/utils/auth-routing.util';
import type { LoginLocalApiResponse } from '@core/api/workspace/models';

const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/;

interface LoginFormModel {
  email: string;
  password: string;
}

@Component({
  selector: 'app-login-page',
  imports: [
    FormField,
    SignalFormDirective,
    RouterLink,
    SignalTextField,
    SignalButton,
    AppSvgIconComponent,
  ],
  templateUrl: './login-page.html',
  styleUrl: './login-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginPage extends BaseComponent implements OnInit {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly platformId = inject(PLATFORM_ID);

  readonly returnUrl = signal<string | undefined>(undefined);

  readonly loginState = new State<LoginLocalApiResponse>();
  readonly RequestState = RequestState;
  readonly googleOnlyMessage = signal<string | null>(null);

  readonly loginModel = signal<LoginFormModel>({
    email: '',
    password: '',
  });

  readonly loginForm = form(this.loginModel, (schemaPath) => {
    required(schemaPath.email, { message: 'This field is required' });
    email(schemaPath.email, { message: 'Enter a valid email address' });

    required(schemaPath.password, { message: 'This field is required' });
    minLength(schemaPath.password, 8, { message: 'Minimum 8 characters required' });
    maxLength(schemaPath.password, 128, { message: 'Maximum 128 characters allowed' });
    pattern(schemaPath.password, PASSWORD_PATTERN, {
      message: 'Password must include uppercase, lowercase, and a number',
    });
  });

  readonly currentYear = new Date().getFullYear();

  ngOnInit(): void {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (returnUrl?.startsWith('/') && !returnUrl.startsWith('//')) {
      this.returnUrl.set(returnUrl);
    }

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.tokenStorage.isAuthenticated()) {
      return;
    }

    if (this.tokenStorage.hasWorkspaceSession()) {
      void navigateAfterLogin(this.router, {
        workspace: this.tokenStorage.getWorkspace() ?? undefined,
        returnUrl: this.returnUrl(),
      });
      return;
    }

    void navigateAfterUserAuth(this.router, this.userAPIService, this.tokenStorage, {
      workspaceAccessCount: this.tokenStorage.getWorkspaceAccessCount(),
      workspaceIds: this.tokenStorage.getWorkspaceIds(),
      returnUrl: this.returnUrl(),
    });
  }

  onGoogleSignInClicked(): void {
    const params = new URLSearchParams({
      purpose: 'LOGIN',
      returnUrl: this.returnUrl() ?? '/dashboard',
    });
    window.location.href = `${environment.baseUrl}/auth/google?${params}`;
  }

  onSubmit(): void {
    if (!validateSignalForm(this.loginForm)) return;

    this.googleOnlyMessage.set(null);
    const { email, password } = this.loginModel();

    this.executeRequest({
      state: this.loginState,
      request: this.userAPIService.loginLocal({ email, password }),
      showLoader: true,
      handleError: false,
      onSuccess: async (response) => {
        this.tokenStorage.setUserSession(response.data);
        await navigateAfterUserAuth(this.router, this.userAPIService, this.tokenStorage, {
          workspace: response.data.workspace,
          workspaceAccessCount: response.data.workspaceAccessCount,
          workspaceIds: response.data.workspaceIds,
          returnUrl: this.returnUrl(),
        });
      },
      onFailed: (error) => this.handleLoginError(error, email),
    });
  }

  private handleLoginError(error: HttpErrorResponse, email: string): void {
    if (error.status === 404) {
      this.router.navigate(['/auth/no-account'], {
        queryParams: { email },
      });
      return;
    }

    const messages = error.error?.errorMessages ?? [];
    const message = messages[0] ?? 'Sign-in failed. Please try again.';

    if (error.status === 400 && message.includes('Google')) {
      this.googleOnlyMessage.set(message);
      return;
    }

    this.toasterService.error(message);
  }
}
